import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Pagination from "../components/Pagination";
import PageHeader from "../components/PageHeader";
import ConfirmModal from "../components/ConfirmModal";
import DocumentDetailsModal from "../components/DocumentDetailsModal";
import {
  KnowledgeDocument,
  KnowledgeDocumentsResponse,
  PaginationInfo,
  DocumentStatistics,
} from "../types/knowledge";
import "./KnowledgeDocumentsPage.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";

const KnowledgeDocumentsPage: React.FC = () => {
  const { user, token, tenantId, logout } = useAuth();
  const navigate = useNavigate();

  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    page_size: 20,
    total_documents: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    next_page: null,
    previous_page: null,
  });
  const [statistics, setStatistics] = useState<DocumentStatistics>({
    total_documents: 0,
    by_status: {},
    by_file_type: {},
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [fileTypeFilter, setFileTypeFilter] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [orderBy, setOrderBy] = useState<string>("-uploaded_at");

  // Delete confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Document details modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!user?.is_superuser) {
      navigate("/chats");
      return;
    }
    fetchDocuments();
  }, [
    pagination.page,
    pagination.page_size,
    statusFilter,
    fileTypeFilter,
    searchQuery,
    orderBy,
  ]);

  const fetchDocuments = async () => {
    if (!token) return;

    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      params.append("page", pagination.page.toString());
      params.append("page_size", pagination.page_size.toString());

      if (statusFilter) params.append("status", statusFilter);
      if (fileTypeFilter) params.append("file_type", fileTypeFilter);
      if (searchQuery) params.append("search", searchQuery);
      if (orderBy) params.append("order_by", orderBy);

      const response = await fetch(
        `${API_BASE_URL}/text-extraction/documents/?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            ...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch documents");
      }

      const data: KnowledgeDocumentsResponse = await response.json();

      if (data.status && data.data) {
        setDocuments(data.data.documents);
        setPagination(data.data.pagination);
        setStatistics(data.data.statistics);
      } else {
        throw new Error(data.message || "Failed to load documents");
      }
    } catch (err) {
      console.error("Error fetching documents:", err);
      setError(err instanceof Error ? err.message : "Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (size: number) => {
    setPagination((prev) => ({ ...prev, page_size: size, page: 1 }));
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchDocuments();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleDeleteClick = (documentId: string, documentName: string) => {
    setDocumentToDelete({ id: documentId, name: documentName });
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!token || !documentToDelete) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/text-extraction/documents/${documentToDelete.id}/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            ...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete document");
      }

      // Close modal and refresh the documents list
      setShowDeleteConfirm(false);
      setDocumentToDelete(null);
      fetchDocuments();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete document",
      );
      setShowDeleteConfirm(false);
      setDocumentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDocumentToDelete(null);
  };

  const handleDetailsClick = (documentId: string) => {
    setSelectedDocumentId(documentId);
    setShowDetailsModal(true);
  };

  const handleDetailsClose = () => {
    setShowDetailsModal(false);
    setSelectedDocumentId(null);
  };

  if (!user?.is_superuser) {
    return null;
  }

  return (
    <div className="knowledge-documents-container">
      <PageHeader
        title="Knowledge Documents Management"
        showBackButton={true}
        backTo="/chats"
        username={user?.username || "User"}
        isSuperuser={user?.is_superuser}
        onLogout={async () => {
          await logout();
          navigate("/login");
        }}
        onManageKnowledge={() => navigate("/knowledge-documents")}
        onManageUsers={() => navigate("/manage-users")}
      />

      <main className="knowledge-main">
        {/* Action Bar */}
        <div className="action-bar">
          <button
            className="upload-files-button"
            onClick={() => navigate("/upload-files")}
          >
            📤 Upload New Files
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="statistics-cards">
          <div className="stat-card">
            <h3>Total Documents</h3>
            <p className="stat-value">{statistics.total_documents}</p>
          </div>
          <div className="stat-card">
            <h3>By Status</h3>
            <div className="stat-breakdown">
              {Object.entries(statistics.by_status).map(([status, count]) => (
                <div key={status} className="stat-item">
                  <span className={`status-badge ${status}`}>{status}</span>
                  <span>{count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="stat-card">
            <h3>By File Type</h3>
            <div className="stat-breakdown">
              {Object.entries(statistics.by_file_type).map(([type, count]) => (
                <div key={type} className="stat-item">
                  <span className="file-type-badge">{type.toUpperCase()}</span>
                  <span>{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <form onSubmit={handleSearchSubmit} className="search-form">
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-button">
              Search
            </button>
          </form>

          <div className="filters">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="filter-select"
            >
              <option value="">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>

            <select
              value={fileTypeFilter}
              onChange={(e) => {
                setFileTypeFilter(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="filter-select"
            >
              <option value="">All File Types</option>
              <option value="pdf">PDF</option>
              <option value="docx">DOCX</option>
              <option value="xlsx">XLSX</option>
            </select>

            <select
              value={orderBy}
              onChange={(e) => {
                setOrderBy(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="filter-select"
            >
              <option value="-uploaded_at">Newest First</option>
              <option value="uploaded_at">Oldest First</option>
              <option value="-file_size">Largest First</option>
              <option value="file_size">Smallest First</option>
              <option value="name">Name (A-Z)</option>
              <option value="-name">Name (Z-A)</option>
            </select>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Documents Table */}
        {loading ? (
          <div className="loading-state">Loading documents...</div>
        ) : documents.length === 0 ? (
          <div className="empty-state">
            <h3>No documents found</h3>
            <p>Try adjusting your filters or search query</p>
          </div>
        ) : (
          <>
            <div className="documents-table-container">
              <table className="documents-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Size</th>
                    <th>Status</th>
                    <th>Uploaded</th>
                    <th>Chunks</th>
                    <th>Embedding Progress</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id}>
                      <td className="doc-name">
                        <div className="doc-name-container">
                          <span className="doc-filename">{doc.name}</span>
                          {doc.metadata?.original_filename && (
                            <span className="doc-original-name">
                              {doc.metadata.original_filename}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="file-type-badge">
                          {doc.file_type.toUpperCase()}
                        </span>
                      </td>
                      <td>{formatFileSize(doc.file_size)}</td>
                      <td>
                        <span className={`status-badge ${doc.status}`}>
                          {doc.status}
                        </span>
                      </td>
                      <td className="date-cell">
                        {formatDate(doc.uploaded_at)}
                      </td>
                      <td>
                        {doc.chunks_with_embeddings} / {doc.total_chunks}
                      </td>
                      <td>
                        <div className="progress-container">
                          <div
                            className="progress-bar"
                            style={{ width: `${doc.embedding_progress}%` }}
                          ></div>
                          <span className="progress-text">
                            {doc.embedding_progress.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                      <td className="actions-cell">
                        <div className="action-buttons">
                          <button
                            onClick={() => handleDetailsClick(doc.id)}
                            className="action-button details"
                            title="View Details"
                          >
                            Details
                          </button>
                          <a
                            href={doc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="action-button view"
                            title="View Document"
                          >
                            View
                          </a>
                          <button
                            onClick={() => handleDeleteClick(doc.id, doc.name)}
                            className="action-button delete"
                            title="Delete Document"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.total_pages}
              hasNext={pagination.has_next}
              hasPrevious={pagination.has_previous}
              onPageChange={handlePageChange}
              pageSize={pagination.page_size}
              onPageSizeChange={handlePageSizeChange}
              totalItems={pagination.total_documents}
            />
          </>
        )}
      </main>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Document"
        message={`Are you sure you want to delete "${documentToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        variant="danger"
      />

      <DocumentDetailsModal
        isOpen={showDetailsModal}
        documentId={selectedDocumentId}
        token={token || ""}
        tenantId={tenantId || ""}
        onClose={handleDetailsClose}
      />
    </div>
  );
};

export default KnowledgeDocumentsPage;
